import { z } from "zod";

export interface Address {
  street_number?: string;
  route?: string;
  locality?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  country_code?: string;
}

export const DEFAULT_CONTRACTOR_ADDRESS: Address = {
  country: "United Kingdom",
  country_code: "GB",
};

// Unified ContractorEntity interface used across all components
export interface ContractorEntity {
  id: number;
  status: string;
  businessName: string;
  registrationNumber?: string;
  
  // Tax registration fields (country-specific)
  vatRegistered: boolean; // UK VAT registration
  vatNumber?: string; // UK VAT number
  w9Acknowledged?: boolean; // US W-9 form acknowledgment
  taxId?: string; // US Tax ID (EIN/SSN)
  salesTaxRegistered?: boolean; // Sales tax registration for other countries
  salesTaxNumber?: string; // Sales tax number for other countries
  taxRegistered?: boolean; // Generic tax registration for other countries
  taxNumber?: string; // Generic tax number for other countries
  
  yearlyTurnover?: number;
  registeredAddress: Address;
  tradingAddress?: Address;
  bankDetails?: string;
  createdAt: string;
  updatedAt: string;
}

// Response interfaces
export interface ContractorEntityResponse {
  status: string;
  message: string;
  entityStatus: string;
  entityId: number;
}

export interface GetContractorEntityResponse {
  hasEntity: boolean;
  entity?: ContractorEntity;
}

export const contractorEntityFormSchema = z
  .object({
    // Step 1: Entity Status
    hasExistingEntity: z.boolean(),

    // Step 2: Entity Details
    entityDetails: z.object({
      // For existing entities
      businessName: z.string().optional(),
      registrationNumber: z.string().optional(),
      // For new entities
      proposedBusinessName: z.string().optional(),

      // Tax registration fields (country-specific)
      vatRegistered: z.boolean().default(false),
      vatNumber: z.string().optional(),
      w9Acknowledged: z.boolean().default(false),
      taxId: z.string().optional(),
      salesTaxRegistered: z.boolean().default(false),
      salesTaxNumber: z.string().optional(),
      taxRegistered: z.boolean().default(false),
      taxNumber: z.string().optional(),
      yearlyTurnover: z.union([z.number(), z.string().transform(val => val ? Number(val) : undefined)]).optional(),

      registeredAddress: z.object({
        street_number: z.string().optional(),
        route: z.string().min(1, "Street name is required"),
        locality: z.string().min(1, "Town/City is required"),
        state: z.string().optional(),
        postal_code: z.string().min(1, "Postcode is required"),
        country: z.string().optional(),
        country_code: z.string().optional(),
      }),
      tradingAddressDifferent: z.boolean().default(false),
      tradingAddress: z
        .object({
          street_number: z.string().optional(),
          route: z.string().optional(),
          locality: z.string().optional(),
          state: z.string().optional(),
          postal_code: z.string().optional(),
          country: z.string().optional(),
          country_code: z.string().optional(),
        })
        .optional(),

      bankDetails: z.string().optional(),
    }),

    // Step 3: Contact Information
    contactDetails: z.object({
      fullName: z.string().min(2, "Full name must be at least 2 characters"),
      phone: z.string().min(10, "Please enter a valid phone number"),
    }),
  })
  .superRefine((data, ctx) => {
    // Validate business name based on entity status
    if (data.hasExistingEntity) {
      if (
        !data.entityDetails.businessName ||
        data.entityDetails.businessName.length < 2
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Business name is required for existing entities",
          path: ["entityDetails", "businessName"],
        });
      }
      if (
        !data.entityDetails.registrationNumber ||
        data.entityDetails.registrationNumber.length < 1
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Registration number is required for existing entities",
          path: ["entityDetails", "registrationNumber"],
        });
      }
    } else {
      if (
        !data.entityDetails.proposedBusinessName ||
        data.entityDetails.proposedBusinessName.length < 2
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Proposed business name is required for new entities",
          path: ["entityDetails", "proposedBusinessName"],
        });
      }
    }

    // Tax registration validation (country-specific)
    if (data.entityDetails.vatRegistered) {
      if (!data.entityDetails.vatNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "VAT number is required when VAT registered",
          path: ["entityDetails", "vatNumber"],
        });
      }

      if (
        data.entityDetails.yearlyTurnover &&
        data.entityDetails.yearlyTurnover <= 90000
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "VAT registration is only required if yearly turnover exceeds £90,000",
          path: ["entityDetails", "yearlyTurnover"],
        });
      }
    }

    // W-9 validation for US entities
    // If taxId is provided (US-specific field), then W-9 must be acknowledged
    if (data.entityDetails.taxId && data.entityDetails.taxId.trim()) {
      if (!data.entityDetails.w9Acknowledged) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "W-9 form acknowledgment is required for US tax reporting",
          path: ["entityDetails", "w9Acknowledged"],
        });
      }
    }

    if (data.entityDetails.w9Acknowledged) {
      if (!data.entityDetails.taxId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Tax ID (EIN/SSN) is required when W-9 is acknowledged",
          path: ["entityDetails", "taxId"],
        });
      } else {
        // Validate EIN/SSN format
        const taxId = data.entityDetails.taxId.trim();
        const einPattern = /^\d{2}-\d{7}$/; // EIN: XX-XXXXXXX
        const ssnPattern = /^\d{3}-\d{2}-\d{4}$/; // SSN: XXX-XX-XXXX
        const rawDigitsPattern = /^\d{9}$/; // Exactly 9 digits without dashes
        
        if (!einPattern.test(taxId) && !ssnPattern.test(taxId) && !rawDigitsPattern.test(taxId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Tax ID must be in EIN format (XX-XXXXXXX), SSN format (XXX-XX-XXXX), or exactly 9 digits",
            path: ["entityDetails", "taxId"],
          });
        }
      }
    }

    if (data.entityDetails.salesTaxRegistered) {
      if (!data.entityDetails.salesTaxNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Sales tax number is required when sales tax registered",
          path: ["entityDetails", "salesTaxNumber"],
        });
      }
    }

    if (data.entityDetails.taxRegistered) {
      if (!data.entityDetails.taxNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Tax number is required when tax registered",
          path: ["entityDetails", "taxNumber"],
        });
      }
    }

    // Registered address validation
    const registeredAddress = data.entityDetails.registeredAddress;
    if (
      !registeredAddress.route ||
      !registeredAddress.locality ||
      !registeredAddress.postal_code
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Please provide a complete registered address (street name, town/city, and postcode are required)",
        path: ["entityDetails", "registeredAddress"],
      });
    }

    // Trading address validation
    if (data.entityDetails.tradingAddressDifferent) {
      const tradingAddress = data.entityDetails.tradingAddress;
      if (!tradingAddress) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Trading address is required when different from registered address",
          path: ["entityDetails", "tradingAddress"],
        });
      } else {
        // Validate individual trading address fields
        if (!tradingAddress.route) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Street name is required",
            path: ["entityDetails", "tradingAddress", "route"],
          });
        }
        if (!tradingAddress.locality) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Town/City is required",
            path: ["entityDetails", "tradingAddress", "locality"],
          });
        }
        if (!tradingAddress.postal_code) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Postcode is required",
            path: ["entityDetails", "tradingAddress", "postal_code"],
          });
        }
      }
    }

    // Country-specific postcode/ZIP code validation
    const validatePostalCode = (postalCode: string, countryCode?: string) => {
      if (!postalCode) return true;
      
      // Determine country from various sources
      const country = countryCode || 
        registeredAddress.country_code || 
        (registeredAddress.country === 'United States' ? 'US' : 'GB');
      
      if (country === 'US') {
        // US ZIP code validation (5 digits or 5+4 format)
        const usZipRegex = /^\d{5}(-\d{4})?$/;
        return usZipRegex.test(postalCode.replace(/\s/g, ""));
      } else {
        // UK postcode validation (default)
        const ukPostcodeRegex = /^([A-Z][A-HJ-Y]?\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A{2})$/;
        return ukPostcodeRegex.test(postalCode.replace(/\s/g, ""));
      }
    };

    // Validate registered address postal code
    if (
      registeredAddress.postal_code &&
      !validatePostalCode(registeredAddress.postal_code, registeredAddress.country_code)
    ) {
      const country = registeredAddress.country_code || 
        (registeredAddress.country === 'United States' ? 'US' : 'GB');
      const format = country === 'US' ? 'ZIP code format (e.g., 12345 or 12345-6789)' : 'postcode format';
      
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Invalid ${format}`,
        path: ["entityDetails", "registeredAddress", "postal_code"],
      });
    }

    // Validate trading address postal code
    if (
      data.entityDetails.tradingAddressDifferent &&
      data.entityDetails.tradingAddress?.postal_code
    ) {
      const tradingCountry = data.entityDetails.tradingAddress.country_code || 
        registeredAddress.country_code || 
        (registeredAddress.country === 'United States' ? 'US' : 'GB');
        
      if (!validatePostalCode(data.entityDetails.tradingAddress.postal_code, tradingCountry)) {
        const format = tradingCountry === 'US' ? 'ZIP code format (e.g., 12345 or 12345-6789)' : 'postcode format';
        
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid ${format}`,
          path: ["entityDetails", "tradingAddress", "postal_code"],
        });
      }
    }
  });

export type ContractorEntityFormData = z.infer<
  typeof contractorEntityFormSchema
>;
